/**
 * inlineChat — pure decisions behind the inline game chat (GameChatPanel):
 * the chat-only log rows, the keyboard lift, and what the chat toggle does.
 *
 * RN-free for unit testing.
 */
import type { ChatMessageResponse } from '@warcarp/api-types'
import { mergeChatIntoLog, type LogRow } from './logFeed'

/**
 * The chat-only log: every loaded message dressed as a log row (same shape the
 * mixed log uses, so EventLogRow renders it identically), newest first.
 */
export function chatOnlyLogRows(args: {
  messages: readonly ChatMessageResponse[]
  events: readonly { ts: number }[]
  slotByUserId: ReadonlyMap<string, string>
}): LogRow[] {
  return mergeChatIntoLog({ rowsNewestFirst: [], ...args })
}

export type ChatButtonAction = 'toggle-panel' | 'toggle-drawer'

/**
 * The 💬 button toggles the inline chat bar on the live ACTIVE board. Where
 * there is no inline bar (a completed game, or the time-machine snapshot) it
 * opens the full-conversation drawer instead.
 */
export function chatButtonAction(args: { status: string | undefined; inSnapshot: boolean }): ChatButtonAction {
  return args.status === 'ACTIVE' && !args.inSnapshot ? 'toggle-panel' : 'toggle-drawer'
}

/**
 * Whether the inline chat input bar is open under the map controls. Off-turn
 * it follows the remembered per-device mode; on your turn only while you have
 * explicitly opened it this turn. A finished game has no turns left, so the
 * bar is always there for members: the end of a game is when people talk.
 */
export function inlineChatActive(args: {
  canChat: boolean
  status: string | undefined
  inSnapshot: boolean
  isMyTurn: boolean
  chatMode: boolean
  turnChatOpen: boolean
}): boolean {
  if (!args.canChat || args.inSnapshot) return false
  if (args.status === 'COMPLETE') return true
  if (args.status !== 'ACTIVE') return false
  return args.isMyTurn ? args.turnChatOpen : args.chatMode
}

/**
 * When your turn starts, keep the inline chat open (instead of switching to
 * the turn controls) if you were mid-message: chat open with the input
 * focused or unsent text in it.
 */
export function keepChatOnTurnStart(args: { chatWasOpen: boolean; inputFocused: boolean; hasDraft: boolean }): boolean {
  return args.chatWasOpen && (args.inputFocused || args.hasDraft)
}

/** Duration of the bar stack sliding up onto / back off the keyboard. */
export const KEYBOARD_FIT_MS = 250

/** Space between the chat input bar and the keyboard's top edge: none — the
 *  bar sits flush on the keyboard (its own padding gives the input room). */
export const KEYBOARD_GAP = 0

/**
 * Keyboard fit as a transform (no re-layout): how far the bar stack (map bar
 * + chat input) slides up to sit on the keyboard. The map keeps its size; the
 * bars simply cover its bottom edge while you type (tall maps — short ones
 * already clear the keyboard). `scale` stays 1 and is kept for the shared map
 * transform. Never slides above the map's top.
 */

export function keyboardFitTransform(args: {
  hostTop: number
  mapHeight: number
  barsHeight: number
  keyboardTop: number
}): { scale: number; shift: number } {
  const { hostTop, mapHeight, barsHeight, keyboardTop } = args
  const room = keyboardTop - KEYBOARD_GAP - hostTop - barsHeight
  if (mapHeight <= 0 || room >= mapHeight) return { scale: 1, shift: 0 }
  return { scale: 1, shift: Math.round(Math.min(mapHeight, mapHeight - room)) }
}

/** Web Enter-to-send for the inline composer (Shift+Enter keeps a newline). */
export function isWebSendKey(key: string, shiftKey: boolean | undefined): boolean {
  return key === 'Enter' && !shiftKey
}

/** The inline chat field grows to at most this many lines, then scrolls. */
export const BAR_INPUT_MAX_LINES = 3

/**
 * Height of the inline chat field: one line until the text wraps, then one
 * more line per wrapped line (capped), plus its 5 px top/bottom padding and
 * 1 px borders. `contentHeight` is the TextInput's reported content size,
 * which already includes the vertical padding (web and iOS alike — treating
 * native as padding-free rendered a one-line message two lines tall).
 */
export function barInputHeight(contentHeight: number, lineHeight: number, contentIncludesPadding: boolean): number {
  const textHeight = Math.max(0, contentHeight - (contentIncludesPadding ? 10 : 0))
  const lines = Math.min(BAR_INPUT_MAX_LINES, Math.max(1, Math.round(textHeight / lineHeight)))
  return lines * lineHeight + 10 + 2
}
